import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { SimpleForm, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { transformBrandPayload } from '@/components/brand/validators/brandValidators.js';
import { RetailChainBasicInfo } from '@/components/retailChain/forms/RetailChainBasicInfo.jsx';
import { RetailChainBrandDraft } from '@/components/retailChain/forms/RetailChainBrandDraft.jsx';
import { RetailChainConfiguration } from '@/components/retailChain/forms/RetailChainConfiguration.jsx';
import { RetailChainPendingBrands } from '@/components/retailChain/forms/RetailChainPendingBrands.jsx';
import { transformRetailChainPayload } from '@/components/retailChain/validators/retailChainValidators.js';

export const useRetailChainBrandDrafts = () => {
    const brandsRef = useRef([]);
    const [brands, setBrands] = useState([]);

    const saveBrand = brand => {
        const next = [...brandsRef.current, { ...brand, clientId: crypto.randomUUID() }];
        brandsRef.current = next;
        setBrands(next);
    };

    const removeBrand = clientId => {
        const next = brandsRef.current.filter(brand => brand.clientId !== clientId);
        brandsRef.current = next;
        setBrands(next);
    };

    const transform = useCallback(data => {
        const payload = transformRetailChainPayload(data);
        if (brandsRef.current.length > 0) {
            payload.brands = brandsRef.current.map(brand => transformBrandPayload(brand));
        }
        return payload;
    }, []);

    return { brands, saveBrand, removeBrand, transform };
};

const PendingBrandsDirtyState = ({ count }) => {
    const { register, resetField, setValue } = useFormContext();

    useEffect(() => {
        register('pendingBrandCount');
        resetField('pendingBrandCount', { defaultValue: 0 });
    }, [register, resetField]);

    useEffect(() => {
        setValue('pendingBrandCount', count, { shouldDirty: true });
    }, [count, setValue]);

    return null;
};

PendingBrandsDirtyState.propTypes = {
    count: PropTypes.number.isRequired,
};

export const RetailChainForm = ({ handleClose, pendingBrands = [], onSaveBrand, onRemoveBrand }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const isEdit = Boolean(record?.id);
    const [isBrandFormOpen, setIsBrandFormOpen] = useState(false);
    const canAddBrands = typeof onSaveBrand === 'function';

    return (
        <>
            <SimpleForm
                defaultValues={
                    isEdit
                        ? undefined
                        : {
                              forwarderType: 'Default',
                              operators: [],
                              depositWithdrawalMethods: ['cash'],
                          }
                }
                toolbar={<TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />}
            >
                <PendingBrandsDirtyState count={pendingBrands.length} />
                <RetailChainBasicInfo />
                <RetailChainConfiguration />
                {canAddBrands ? <RetailChainPendingBrands brands={pendingBrands} onAdd={() => setIsBrandFormOpen(true)} onRemove={onRemoveBrand} /> : null}
            </SimpleForm>
            {canAddBrands && isBrandFormOpen ? (
                <RetailChainBrandDraft
                    lockedRetailChain={isEdit && record?.id ? { id: record.id, name: record.name } : null}
                    onClose={() => setIsBrandFormOpen(false)}
                    onSave={brand => {
                        onSaveBrand(brand);
                        setIsBrandFormOpen(false);
                    }}
                />
            ) : null}
        </>
    );
};

RetailChainForm.propTypes = {
    handleClose: PropTypes.func,
    pendingBrands: PropTypes.array,
    onSaveBrand: PropTypes.func,
    onRemoveBrand: PropTypes.func,
};

export const RetailChainCreate = ({ handleClose = () => {} }) => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    const { brands, saveBrand, removeBrand, transform } = useRetailChainBrandDrafts();

    if (isLoading || !permissions?.isAdmin) {
        return null;
    }

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.retailChain.labels.create')}>
            <CustomCreate handleClose={handleClose} transform={transform}>
                <RetailChainForm handleClose={handleClose} pendingBrands={brands} onSaveBrand={saveBrand} onRemoveBrand={removeBrand} />
            </CustomCreate>
        </RightDrawer>
    );
};

RetailChainCreate.propTypes = {
    handleClose: PropTypes.func,
};
